import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import type { Recipe } from '../data/types';
import { parseServings } from './scale';

// Household serving-size overrides, stored server-side like stars and notes,
// so a change made on one device is what every device (and every refresh)
// sees. The recipe page shows a loud notice whenever an override is active.

export type ServingsEntry = { servings: number; scaled_by: string | null };

type State = {
  overrides: Record<string, ServingsEntry | undefined>;
  set: (slug: string, servings: number, scaledBy: string | null) => void;
  reset: (slug: string) => void;
};

const Ctx = createContext<State>({ overrides: {}, set: () => {}, reset: () => {} });

export function ServingsProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, ServingsEntry | undefined>>({});

  useEffect(() => {
    api.servings().then(setOverrides).catch(() => {});
  }, []);

  function set(slug: string, servings: number, scaledBy: string | null) {
    const prev = overrides[slug];
    setOverrides((o) => ({ ...o, [slug]: { servings, scaled_by: scaledBy } }));
    api.setServings(slug, servings, scaledBy ?? undefined).catch(() => {
      setOverrides((o) => ({ ...o, [slug]: prev }));
    });
  }

  function reset(slug: string) {
    const prev = overrides[slug];
    setOverrides((o) => ({ ...o, [slug]: undefined }));
    api.clearServings(slug).catch(() => {
      setOverrides((o) => ({ ...o, [slug]: prev }));
    });
  }

  return <Ctx.Provider value={{ overrides, set, reset }}>{children}</Ctx.Provider>;
}

export function useServings() {
  return useContext(Ctx);
}

// Everything a component needs to render one recipe at the current scale.
export function useRecipeScale(recipe: Recipe | undefined) {
  const { overrides, set, reset } = useServings();
  const original = recipe ? parseServings(recipe.yield) : null;
  const entry = recipe ? overrides[recipe.slug] : undefined;
  const current = entry && original ? entry.servings : original;
  const factor = original && current ? current / original : 1;
  return {
    original,                    // null → recipe isn't scalable
    current,
    factor,
    isScaled: factor !== 1,
    scaledBy: entry?.scaled_by ?? null,
    setServings: (n: number, by: string | null) => {
      if (!recipe || !original) return;
      if (n === original) reset(recipe.slug);
      else set(recipe.slug, n, by);
    },
    reset: () => recipe && reset(recipe.slug),
  };
}
